import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';
import { exportRowsToExcel } from '../exportExcel';
import { openStudentHoursPrint } from '../printStudentHours';

const CURRENT_YEAR = new Date().getFullYear();

function formatHour(v) {
  if (v === null || v === undefined) return 0;
  return Number.isInteger(v) ? v : v.toFixed(1);
}

// 화면에 보여줄 숫자: 0이거나 값이 없으면 빈칸
function hv(v) {
  if (v === null || v === undefined || Number(v) === 0) return '';
  return formatHour(v);
}

// 엑셀에 넣을 숫자: 0이면 빈칸, 아니면 숫자 그대로 (엑셀에서 합계/서식에 쓸 수 있도록 문자열이 아닌 숫자로 둔다)
function xv(v) {
  if (v === null || v === undefined || Number(v) === 0) return '';
  return Number(Number(v).toFixed(1));
}

function sumHours(student, chapterList) {
  return chapterList.reduce(
    (acc, c) => {
      const h = student.hours[c.amtcode] || { thrytime: 0, practime: 0 };
      acc.thry += h.thrytime || 0;
      acc.prac += h.practime || 0;
      return acc;
    },
    { thry: 0, prac: 0 }
  );
}

function calcPct(doneThry, donePrac, legalThry, legalPrac) {
  const legalTotal = (legalThry || 0) + (legalPrac || 0);
  if (legalTotal <= 0) return 0;
  const doneTotal = (doneThry || 0) + (donePrac || 0);
  return Math.min(100, Math.round((doneTotal / legalTotal) * 100));
}

function ProgressCell({ pct }) {
  return (
    <td className="hr-progress-cell">
      <div className="hr-mini-progress-wrap">
        <div className="hr-mini-progress-track">
          <div className="hr-mini-progress-fill" style={{ width: `${pct}%` }} />
        </div>
        <span className="hr-mini-progress-pct">{pct}%</span>
      </div>
    </td>
  );
}

export default function StudentHours() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const [grade, setGrade] = useState(3);
  const [classtype, setClasstype] = useState('R');
  const [classOptions, setClassOptions] = useState([]);
  const [classNo, setClassNo] = useState('');
  const [nameFilter, setNameFilter] = useState('');
  const [report, setReport] = useState(null);
  const [showShortfall, setShowShortfall] = useState(false); // 미달 시수 표시(분홍색) 여부. 기본은 표시 안 함
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setClassNo('');
    api.get('/classes', { params: { grade, classtype } }).then((res) => setClassOptions(res.data));
  }, [grade, classtype]);

  useEffect(() => {
    setNameFilter('');
  }, [classNo]);

  useEffect(() => {
    setLoading(true);
    api
      .get('/admin/student-hours-report', {
        params: { year, grade, classtype, class_no: classNo === '' ? undefined : classNo },
      })
      .then((res) => setReport(res.data))
      .finally(() => setLoading(false));
  }, [year, grade, classtype, classNo]);

  const chapters = report?.chapters ?? [];
  const allStudents = report?.students ?? [];
  const nameOptions = Array.from(new Set(allStudents.map((s) => s.name))).sort((a, b) =>
    a.localeCompare(b, 'ko')
  );
  const nameQuery = nameFilter.trim();
  const students = nameQuery
    ? allStudents.filter((s) => s.name.includes(nameQuery))
    : allStudents;

  // 법정시수에 못 미치는 칸은 "미달 시수 표시"를 체크했을 때만 분홍색으로 표시한다
  const below = (isBelow) => (showShortfall && isBelow ? 'hr-below-legal' : '');

  // 과목별 묶음: 같은 과목의 교육내용을 모아 법정시수 소계를 계산한다
  const subjectGroups = useMemo(() => {
    const groups = [];
    chapters.forEach((c) => {
      let g = groups[groups.length - 1];
      if (!g || g.subject !== c.group_subject) {
        g = { subject: c.group_subject, chapters: [], legalThry: 0, legalPrac: 0 };
        groups.push(g);
      }
      g.chapters.push(c);
      g.legalThry += c.legal_thrytime || 0;
      g.legalPrac += c.legal_practime || 0;
    });
    return groups;
  }, [chapters]);

  // PDF 출력은 학생 한 명을 골랐을 때만 가능하다 (반 선택 + 학생이름 선택)
  const singleStudent = nameQuery && students.length === 1 ? students[0] : null;

  const handlePrintPdf = () => {
    if (!singleStudent) return;
    const groups = subjectGroups.map((g) => {
      const sub = sumHours(singleStudent, g.chapters);
      return {
        subject: g.subject,
        legalThry: g.legalThry,
        legalPrac: g.legalPrac,
        thry: sub.thry,
        prac: sub.prac,
        rows: g.chapters.map((c) => {
          const h = singleStudent.hours[c.amtcode] || { thrytime: 0, practime: 0 };
          return {
            chapter: c.amt_chapter,
            legalThry: c.legal_thrytime || 0,
            legalPrac: c.legal_practime || 0,
            thry: h.thrytime || 0,
            prac: h.practime || 0,
          };
        }),
      };
    });
    openStudentHoursPrint(
      {
        year,
        grade,
        classLabel: `${classtype === 'M' ? '면장반' : '정규반'} ${classNo}반`,
        student: { studid: singleStudent.studid, name: singleStudent.name },
      },
      groups,
      {
        legalThry: report?.legal_total_thrytime ?? 0,
        legalPrac: report?.legal_total_practime ?? 0,
        thry: singleStudent.total_thrytime || 0,
        prac: singleStudent.total_practime || 0,
      }
    );
  };

  const handleExport = () => {
    const header1 = ['과목', '교육내용', '법정시수', ''];
    students.forEach((s) => {
      header1.push(`${s.studid} ${s.name}`, '');
      if (nameQuery) header1.push('');
    });
    const header2 = ['', '', '학과', '실습'];
    students.forEach(() => {
      header2.push('학과', '실습');
      if (nameQuery) header2.push('진도율');
    });

    const totalRow = ['', '시수계', xv(report?.legal_total_thrytime ?? 0), xv(report?.legal_total_practime ?? 0)];
    students.forEach((s) => {
      totalRow.push(xv(s.total_thrytime), xv(s.total_practime));
      if (nameQuery) {
        const p = calcPct(s.total_thrytime, s.total_practime, report?.legal_total_thrytime, report?.legal_total_practime);
        totalRow.push(p ? `${p}%` : '');
      }
    });

    // 과목별로 교육내용 행들을 쓰고, 마지막에 과목 소계 행을 붙인다
    const chapterRows = [];
    subjectGroups.forEach((g) => {
      g.chapters.forEach((c, idx) => {
        const row = [idx === 0 ? g.subject : '', c.amt_chapter, xv(c.legal_thrytime), xv(c.legal_practime)];
        students.forEach((s) => {
          const h = s.hours[c.amtcode] || { thrytime: 0, practime: 0 };
          row.push(xv(h.thrytime), xv(h.practime));
          if (nameQuery) {
            const p = calcPct(h.thrytime, h.practime, c.legal_thrytime, c.legal_practime);
            row.push(p ? `${p}%` : '');
          }
        });
        chapterRows.push(row);
      });
      const subRow = ['', '소계', xv(g.legalThry), xv(g.legalPrac)];
      students.forEach((s) => {
        const sub = sumHours(s, g.chapters);
        subRow.push(xv(sub.thry), xv(sub.prac));
        if (nameQuery) {
          const p = calcPct(sub.thry, sub.prac, g.legalThry, g.legalPrac);
          subRow.push(p ? `${p}%` : '');
        }
      });
      chapterRows.push(subRow);
    });

    const perStudentCols = nameQuery ? 3 : 2;
    const merges = [
      { s: { r: 0, c: 0 }, e: { r: 1, c: 0 } }, // 과목
      { s: { r: 0, c: 1 }, e: { r: 1, c: 1 } }, // 교육내용
      { s: { r: 0, c: 2 }, e: { r: 0, c: 3 } }, // 법정시수
    ];
    students.forEach((_, i) => {
      const col = 4 + i * perStudentCols;
      merges.push({ s: { r: 0, c: col }, e: { r: 0, c: col + perStudentCols - 1 } });
    });

    exportRowsToExcel(`${grade}학년_학생시수현황_${year}.xlsx`, [header1, header2, totalRow, ...chapterRows], merges);
  };

  return (
    <div className="page">
      <div className="page-header">
        <h2>학생시수현황</h2>
        <div className="hr-header-actions">
          <button
            className="btn-ghost"
            type="button"
            onClick={handlePrintPdf}
            disabled={!singleStudent}
            title={singleStudent ? '선택한 학생의 시수현황을 PDF로 저장/인쇄합니다' : '반과 학생이름을 선택하면 출력할 수 있습니다'}
          >
            PDF 파일 출력
          </button>
          <button className="btn-ghost" type="button" onClick={handleExport}>Excel로 내보내기</button>
        </div>
      </div>

      <div className="card">
        <div className="board-header hr-header-row">
          <h3>{grade}학년 시수현황 ({year}년, 법정시수 : {formatHour((report?.legal_total_thrytime ?? 0) + (report?.legal_total_practime ?? 0))})</h3>
          <div className="form-row hr-filter-row">
            <div className="form-field form-field-narrow hr-shortfall-field">
              <label htmlFor="hr-shortfall">미달 시수 표시</label>
              <div className="hr-shortfall-check">
                <input
                  id="hr-shortfall"
                  type="checkbox"
                  checked={showShortfall}
                  onChange={(e) => setShowShortfall(e.target.checked)}
                />
              </div>
            </div>
            <div className="form-field form-field-narrow">
              <label>연도</label>
              <select value={year} onChange={(e) => setYear(Number(e.target.value))}>
                {[CURRENT_YEAR - 1, CURRENT_YEAR, CURRENT_YEAR + 1].map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
            <div className="form-field form-field-narrow">
              <label>학년</label>
              <select value={grade} onChange={(e) => setGrade(Number(e.target.value))}>
                <option value={1}>1학년</option>
                <option value={2}>2학년</option>
                <option value={3}>3학년</option>
              </select>
            </div>
            <div className="form-field form-field-narrow">
              <label>구분</label>
              <select value={classtype} onChange={(e) => setClasstype(e.target.value)}>
                <option value="R">정규반</option>
                <option value="M">면장반</option>
              </select>
            </div>
            <div className="form-field form-field-narrow">
              <label>반</label>
              <select value={classNo} onChange={(e) => setClassNo(e.target.value)}>
                <option value="">전체</option>
                {classOptions.map((c) => (
                  <option key={c} value={c}>{c}반</option>
                ))}
              </select>
            </div>
            <div className="form-field form-field-narrow">
              <label>학생이름</label>
              <select
                className="field-input"
                value={nameFilter}
                onChange={(e) => setNameFilter(e.target.value)}
                disabled={classNo === ''}
              >
                <option value="">전체</option>
                {nameOptions.map((n) => (
                  <option key={n} value={n}>{n}</option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <div className="table-scroll hours-table-scroll">
          <table className="data-table hours-report-table">
            <thead>
              <tr>
                <th className="hr-sticky hr-col-subject" rowSpan={2}>과목</th>
                <th className="hr-sticky hr-col-midcat" rowSpan={2}>교육내용</th>
                <th className="hr-sticky hr-col-legal" colSpan={2}>법정시수</th>
                {students.map((s) => (
                  <th key={s.studid} colSpan={nameQuery ? 3 : 2} className="hr-student-header">
                    <div>{s.studid}</div>
                    <div>{s.name}</div>
                  </th>
                ))}
              </tr>
              <tr>
                <th className="hr-sticky hr-col-legal-thry">학과</th>
                <th className="hr-sticky hr-col-legal-prac">실습</th>
                {students.map((s) => (
                  <React.Fragment key={s.studid}>
                    <th className="hr-subhead">학과</th>
                    <th className="hr-subhead">실습</th>
                    {nameQuery && <th className="hr-subhead">진도율</th>}
                  </React.Fragment>
                ))}
              </tr>
            </thead>
            <tbody>
              <tr className="hr-total-row">
                <td className="hr-sticky hr-col-subject"></td>
                <td className="hr-sticky hr-col-midcat"><span className="hr-total-badge">시수계</span></td>
                <td className="hr-sticky hr-col-legal-thry">{hv(report?.legal_total_thrytime ?? 0)}</td>
                <td className="hr-sticky hr-col-legal-prac">{hv(report?.legal_total_practime ?? 0)}</td>
                {students.map((s) => (
                  <React.Fragment key={s.studid}>
                    <td className={below(s.total_thrytime < (report?.legal_total_thrytime ?? 0))}>
                      {hv(s.total_thrytime)}
                    </td>
                    <td className={below(s.total_practime < (report?.legal_total_practime ?? 0))}>
                      {hv(s.total_practime)}
                    </td>
                    {nameQuery && (
                      <ProgressCell
                        pct={calcPct(
                          s.total_thrytime,
                          s.total_practime,
                          report?.legal_total_thrytime,
                          report?.legal_total_practime
                        )}
                      />
                    )}
                  </React.Fragment>
                ))}
              </tr>

              {subjectGroups.map((g) => (
                <React.Fragment key={g.subject}>
                  {g.chapters.map((c, idx) => (
                    <tr key={c.amtcode}>
                      <td className="hr-sticky hr-col-subject">{idx === 0 ? g.subject : ''}</td>
                      <td className="hr-sticky hr-col-midcat">{c.amt_chapter}</td>
                      <td className="hr-sticky hr-col-legal-thry">{hv(c.legal_thrytime)}</td>
                      <td className="hr-sticky hr-col-legal-prac">{hv(c.legal_practime)}</td>
                      {students.map((s) => {
                        const h = s.hours[c.amtcode] || { thrytime: 0, practime: 0 };
                        return (
                          <React.Fragment key={s.studid}>
                            <td className={below(h.thrytime < c.legal_thrytime)}>
                              {hv(h.thrytime)}
                            </td>
                            <td className={below(h.practime < c.legal_practime)}>
                              {hv(h.practime)}
                            </td>
                            {nameQuery && (
                              <ProgressCell
                                pct={calcPct(h.thrytime, h.practime, c.legal_thrytime, c.legal_practime)}
                              />
                            )}
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))}
                  <tr className="hr-subtotal-row">
                    <td className="hr-sticky hr-col-subject"></td>
                    <td className="hr-sticky hr-col-midcat">소계</td>
                    <td className="hr-sticky hr-col-legal-thry">{hv(g.legalThry)}</td>
                    <td className="hr-sticky hr-col-legal-prac">{hv(g.legalPrac)}</td>
                    {students.map((s) => {
                      const sub = sumHours(s, g.chapters);
                      return (
                        <React.Fragment key={s.studid}>
                          <td className={below(sub.thry < g.legalThry)}>{hv(sub.thry)}</td>
                          <td className={below(sub.prac < g.legalPrac)}>{hv(sub.prac)}</td>
                          {nameQuery && (
                            <ProgressCell pct={calcPct(sub.thry, sub.prac, g.legalThry, g.legalPrac)} />
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tr>
                </React.Fragment>
              ))}

              {!loading && chapters.length === 0 && (
                <tr>
                  <td colSpan={4} className="empty-row">지정과목 매핑 데이터가 없습니다.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        {!loading && students.length === 0 && (
          <div className="form-message">
            {nameQuery ? '이름이 일치하는 학생이 없습니다.' : '선택한 학년/반에 해당하는 재학생이 없습니다.'}
          </div>
        )}
      </div>
    </div>
  );
}
